import React from "react";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Activity } from "lucide-react";

type AnalyticsErrorBoundaryProps = {
  readonly children: React.ReactNode;
};

type AnalyticsErrorBoundaryState = {
  readonly hasError: boolean;
  readonly retryKey: number;
};

export class AnalyticsErrorBoundary extends React.Component<
  AnalyticsErrorBoundaryProps,
  AnalyticsErrorBoundaryState
> {
  public state: AnalyticsErrorBoundaryState = { hasError: false, retryKey: 0 };

  public static getDerivedStateFromError(): AnalyticsErrorBoundaryState {
    return { hasError: true, retryKey: 0 };
  }

  public componentDidCatch(error: Error): void {
    console.error("[Frontend][Progress] Dashboard failed to render", {
      error: error.message,
    });
  }

  private handleRetry = (): void => {
    this.setState((current) => ({
      hasError: false,
      retryKey: current.retryKey + 1,
    }));
  };

  public render(): React.ReactNode {
    if (this.state.hasError) {
      return (
        <Card className="flex flex-col items-center gap-3 border-dashed p-6 text-center" data-testid="progress-error-state">
          <div className="rounded-full bg-warning/10 p-3 text-warning">
            <Activity size={24} />
          </div>
          <div>
            <h2 className="text-base font-semibold text-text">Progress could not load</h2>
            <p className="mt-1 text-sm leading-relaxed text-text-muted">
              Your entries are safe. Try loading this view again.
            </p>
          </div>
          <Button variant="secondary" size="md" onClick={this.handleRetry}>
            Retry
          </Button>
        </Card>
      );
    }

    return <React.Fragment key={this.state.retryKey}>{this.props.children}</React.Fragment>;
  }
}
